import { Button, CircularProgress, Dialog, DialogActions, DialogContent, Divider, Grid, IconButton } from '@mui/material'
import React, { useEffect, useState } from 'react'
import DashboardContainer from '../../components/DashboardContainer'
import PageContainer from '../../components/PageContainer'
import { AddorUpdateRecord, getList } from '../../redux/api/system'
import { Formik, Form } from 'formik'
import { useSnackbar } from 'notistack'
import { DataGrid } from '@mui/x-data-grid'
import TextInput from '../../components/common/TextInput'
import SelectInput from '../../components/common/SelectInput'
import { Add, Delete, Edit, Label } from '@mui/icons-material'
import { dataTypeOptions } from '../../util';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import CustomDataGrid from '../../components/CustomDataGrid'

const Domains = () => {
    const [ showModal, setShowModal ] = useState(false)
    const [ selectedDomain, setSelectedDomain ] = useState(null);
    const [ records, setRecords ] = useState([]);
    const { enqueueSnackbar } = useSnackbar();
    const [ processing, setProcessing ] = useState(false)
    const [ deleteModal, setDeleteModal ] = useState(false);
    const [ isDeleting, setIsDeleting ] = useState(false);
    const defaultDomain = {
        "domainName": "", "accessFlag":""
    }

    const columns = [
        { 
            field: 'sn', headerName: 'S/N', flex: 0.1, sortable: false,
            renderCell: (params) => (
                <p
                    style={{ margin: 'auto' }}
                >
                    {params?.row?.sn}
                </p>
            )
        },
        
        {
            field: 'actions', headerName: 'Actions', flex: 0.2, sortable: false,
            renderCell: (params) => (
                <div className='action-icons' style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', width: '100%', padding: '0 0.5rem' }}>
                    <IconButton
                        size='small'
                        color="primary"
                        variant="outlined" title='Edit Domain' className='p-0 m-0'
                        onClick={() => {
                            setSelectedDomain(params?.row || defaultDomain);
                            setShowModal(true)
                        }}
                    >
                        <Edit />
                    </IconButton>
                    <IconButton
                        size='small'
                        color="error"
                        variant="outlined" title='Delete Domain' className='p-0 m-0'
                        onClick={() => {
                            setSelectedDomain(params?.row ?? defaultDomain);
                            setDeleteModal(true)
                        }}
                    >
                        <PublicOffIcon />
                    </IconButton>
                </div>
            )
        },
        { field: 'domainName', headerName: 'Domain Name', flex: 1 },
        { field: 'accessFlag', headerName: 'Access Flag', flex: 0.8 },
    ];

    const init = async () => {
        try {
            setProcessing(true)
            const resp = await getList("domains");
            if(resp?.responseCode === "00") {
                setRecords(resp?.listing || [])
            } else {
                enqueueSnackbar(resp?.message || resp?.responseMessage || 'An error occurred', { variant: 'error' })
            }
        }
        catch (err) {
            enqueueSnackbar(err?.message || err?.responseMessage || 'An error occurred', { variant: 'error' })
        }
        finally {
            setProcessing(false)
        }
    }

    const createOrUpdateDomain = async (values, { resetForm }) => {
        try {
            const resp = await AddorUpdateRecord("domain", values?.domainID ? "update" : 'add', values);
            if(resp?.responseCode === "00") {
                init();
                setSelectedDomain(null)
                setShowModal(false)
                enqueueSnackbar('Action performed successfully')
            } else {
                enqueueSnackbar(resp?.message || resp?.responseMessage || 'An error occurred', { variant: 'error' })
            }
        }
        catch (err) {
            enqueueSnackbar(err?.message || err?.responseMessage || 'An error occurred', { variant: 'error' })
        }
    }

    const deleteDomain = async () => {
        try {
            setIsDeleting(true);

            const res = await AddorUpdateRecord("domain", "delete", selectedDomain);

            if(res.responseCode === "00") {
                init();
                setSelectedDomain(null);
                setDeleteModal(false);
                enqueueSnackbar('Domain deleted successfully', { variant: 'success' });
            }
            else {
                enqueueSnackbar(res?.message ?? res?.responseMessage ?? 'An error occurred', { variant: 'error' })
            }
        }
        catch (err) {
            enqueueSnackbar(err?.message ?? err?.responseMessage ?? 'An error occurred', { variant: 'error' })
        }
        finally {
            setIsDeleting(false);
        }
    }

    useEffect(() => {
        init()
    }, [])

    return (
        <PageContainer pageTitle='Dataset Domains' processing={processing}>
            <section className="inner-banner parallax-section">
                <div className='overlay'>
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12 col-sm-12">
                                <h3 className="wow bounceIn" data-wow-delay="0.9s">All</h3>
                                <h1 className="wow fadeInUp text-uppercase conference mb-05 mt-05" data-wow-delay="1.6s">Domains</h1>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <div className="services-area section-padding pt-3">
                <div className="container">
                    <div className="row">
                        <div className='col-12 text-align-right'>
                            <Button variant="contained" onClick={() => { setSelectedDomain(defaultDomain); setShowModal(true) }}>Create Domain</Button>
                        </div>
                        <div className='col-12 mt-4'>
                            <div style={{ height: 550, width: '100%', textAlign: 'center' }}>
                                <CustomDataGrid 
                                    columns={columns}
                                    rows={records}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={showModal} maxWidth="lg">
                <Formik enableReinitialize initialValues={selectedDomain} onSubmit={createOrUpdateDomain}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent className='pl-4 pr-4'>
                                <h3 className='text-bold mt-05' >{values?.domainID ? 'Update' : 'Create'} Dataset Domain</h3>
                                <Divider className="mt-05 mb-2" />
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextInput id="domainName" required label="Domain Name" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="accessFlag" required label="Tag or Short code" />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions className='pr-3 pb-3'>
                                <Button disabled={isSubmitting} onClick={() => { if(!isSubmitting) { setShowModal(false); setSelectedDomain(null) }}} color="secondary" variant="contained" >Cancel</Button>
                                <Button type='submit' variant="contained" disabled={isSubmitting} >
                                    {
                                        isSubmitting ?
                                        <CircularProgress size={15} color="inherit" />
                                        :
                                        values?.domainID ? 'Update Dataset Domain' : 'Add Dataset Domain'
                                    }
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog open={deleteModal} maxWidth="lg">
                <DialogContent className='pl-4 pr-4'>
                    <p className='text-bold mt-05' style={{ fontSize: "1.2em" }} >Are you sure you want to <b style={{ color: "red" }}>delete the Domain</b> from the system</p>
                    <Divider className="mt-2" />
                </DialogContent>
                <DialogActions className='pr-3 pb-3'>
                    <Button 
                        disabled={isDeleting} 
                        onClick={() => { 
                            if(!isDeleting) { 
                                setDeleteModal(false); 
                                setSelectedDomain(null) 
                            }}} 
                        color="secondary" 
                        variant="contained" >
                            Cancel
                        </Button>
                    <Button variant="contained" disabled={isDeleting} onClick={deleteDomain} >
                        {
                            isDeleting ?
                            <CircularProgress size={15} color="inherit" />
                            :
                            'Delete Domain'
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    )
}

export default Domains